import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../api";
import { Spinner } from "../../components/Logo";
import { PageHeader, StatusChip, LevelChip } from "../../components/ui";
import { Modal, Field, ConfirmButton } from "../../components/Modal";
import { AttachmentUploader } from "../../components/Uploads";
import { RichTextEditor } from "../../components/RichTextEditor";
import { Plus, Pencil, Trash2, Flag } from "lucide-react";

const empty = { title: "", category: "Web", level: "Beginner", briefing: "", flag: "", xp_reward: 200, hint_text: "", hint_cost: 20, walkthrough: "", status: "published" };
const cats = ["Web", "Network", "Cryptography", "Forensics", "OSINT", "Social Engineering", "Cloud"];

export default function AdminCTF() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const load = () => api.get("/admin/ctf/challenges").then((r) => setRows(r.data.challenges)).catch(() => {});
  useEffect(() => { load(); }, []);

  const loadAttachments = (id) => api.get(`/admin/ctf/challenges/${id}/attachments`).then((r) => setAttachments(r.data.attachments)).catch(() => {});
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setForm(empty); setEditId(null); setAttachments([]); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ ...c, flag: "" });
    setAttachments([]);
    loadAttachments(c.id);
    setOpen(true);
    api.get(`/admin/ctf/challenges/${c.id}`)
      .then((r) => setForm({ ...r.data.challenge, flag: "" }))
      .catch((e) => toast.error(apiError(e, "Could not load challenge details")));
  };

  const save = async () => {
    const payload = {
      title: form.title,
      category: form.category,
      level: form.level,
      briefing: form.briefing || "",
      xp_reward: Number(form.xp_reward) || 0,
      hint_text: form.hint_text || "",
      hint_cost: Number(form.hint_cost) || 0,
      walkthrough: form.walkthrough || "",
      status: form.status,
    };
    if (form.flag) payload.flag = form.flag;
    try {
      if (editId) await api.patch(`/admin/ctf/challenges/${editId}`, payload);
      else await api.post("/admin/ctf/challenges", payload);
      toast.success(editId ? "Challenge updated" : "Challenge created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/ctf/challenges/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div data-testid="admin-ctf">
      <PageHeader overline="Content" title="CTF challenges" desc="Create sandboxed challenges. Flags are stored hashed and never exposed to clients."
        actions={<button className="btn-primary" onClick={openNew} data-testid="new-ctf-btn"><Plus size={18} /> New challenge</button>} />

      {!rows ? <Spinner /> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="text-slate-400 uppercase text-xs tracking-wider border-b border-white/10">
              <th className="py-3 px-4">Title</th><th className="px-4">Category</th><th className="px-4">Level</th><th className="px-4">Attempts</th><th className="px-4">Solves</th><th className="px-4">Status</th><th className="px-4 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`ctf-row-${c.id}`}>
                  <td className="py-3 px-4 font-medium">{c.title}</td>
                  <td className="px-4 text-slate-400">{c.category}</td>
                  <td className="px-4"><LevelChip level={c.level} /></td>
                  <td className="px-4 font-mono">{c.attempts}</td>
                  <td className="px-4 font-mono text-emerald-400">{c.solves}</td>
                  <td className="px-4"><StatusChip status={c.status} /></td>
                  <td className="px-4"><div className="flex items-center justify-end gap-1">
                    <button className="btn-ghost !px-2" onClick={() => openEdit(c)} data-testid={`edit-ctf-${c.id}`}><Pencil size={16} /></button>
                    <ConfirmButton className="btn-ghost !px-2 text-red-400" onConfirm={() => del(c.id)}><Trash2 size={16} /></ConfirmButton>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="p-10 text-center text-slate-500 flex flex-col items-center gap-3"><Flag size={28} /> No challenges yet.</div>}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit challenge" : "New CTF challenge"} size="xl">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <Field label="Title"><input className="input" value={form.title} onChange={set("title")} data-testid="ctf-title" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category"><select className="input" value={form.category} onChange={set("category")} data-testid="ctf-category">{cats.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Level"><select className="input" value={form.level} onChange={set("level")} data-testid="ctf-level">{["Beginner", "Intermediate", "Advanced", "Expert"].map((l) => <option key={l}>{l}</option>)}</select></Field>
          </div>
          <Field label="Briefing">
            <div data-testid="ctf-briefing">
              <RichTextEditor value={form.briefing} onChange={(html) => setForm({ ...form, briefing: html })} placeholder="Describe the challenge. Use the toolbar for headings, lists, code blocks and images..." />
            </div>
          </Field>
          <Field label={editId ? "Flag (leave blank to keep current)" : "Flag"}><input className="input font-mono" value={form.flag} onChange={set("flag")} placeholder="flag{...}" data-testid="ctf-flag" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="XP reward"><input className="input" type="number" value={form.xp_reward} onChange={set("xp_reward")} data-testid="ctf-xp" /></Field>
            <Field label="Hint cost (XP)"><input className="input" type="number" value={form.hint_cost} onChange={set("hint_cost")} data-testid="ctf-hint-cost" /></Field>
          </div>
          <Field label="Hint"><input className="input" value={form.hint_text || ""} onChange={set("hint_text")} data-testid="ctf-hint-text" /></Field>
          <Field label="Walkthrough (shown after solve)"><textarea className="input min-h-16" value={form.walkthrough || ""} onChange={set("walkthrough")} data-testid="ctf-walkthrough" /></Field>
          <Field label="Status"><select className="input" value={form.status} onChange={set("status")} data-testid="ctf-status">{["published", "draft", "archived","private"].map((s) => <option key={s}>{s}</option>)}</select></Field>
          {editId ? (
            <Field label="Attachments (files learners can download)">
              <AttachmentUploader challengeId={editId} attachments={attachments} onChange={() => loadAttachments(editId)} />
            </Field>
          ) : (
            <p className="text-xs text-slate-500">Save the challenge first, then reopen it to add downloadable attachments.</p>
          )}
          <button className="btn-primary w-full" onClick={save} data-testid="save-ctf-btn">{editId ? "Update" : "Create"} challenge</button>
        </div>
      </Modal>
    </div>
  );
}
